package features.teams.ui.modals

import kotlinx.browser.document
import kotlinx.html.*
import kotlinx.html.dom.append
import kotlinx.html.js.onClickFunction
import org.w3c.dom.HTMLElement
import shared.ui.ModalManager
import AppState
import Router

object CreateTeamModal {
    fun render(root: HTMLElement) {
        root.append {
            div("modal-content fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-dark-800 border border-dark-700 rounded-2xl p-6 w-full max-w-md z-[101] hidden shadow-2xl scale-95 opacity-0 transition-all duration-300") {
                id = "create-team-modal"
                
                div("flex justify-between items-center mb-6") {
                    h3("text-xl font-bold") { +"Create New Team" }
                    button(classes = "text-slate-400 hover:text-white") {
                        onClickFunction = { ModalManager.close() }
                        +"✕"
                    }
                }
                
                div("mb-6") {
                    input(classes = "w-full bg-dark-900 border border-dark-700 rounded-xl p-3 mb-4 focus:outline-none focus:border-brand-cyan transition text-white") {
                        id = "team-name-input"
                        placeholder = "Team Name"
                    }
                    
                    div("flex items-center gap-4") {
                        div("relative w-20 h-20 rounded-xl bg-dark-900 border border-dark-700 overflow-hidden flex items-center justify-center group") {
                            img {
                                id = "team-avatar-preview"
                                classes = setOf("w-full", "h-full", "object-cover", "hidden")
                            }
                            span("text-3xl") {
                                id = "team-avatar-placeholder"
                                +"🏢"
                            }
                        }
                        div("flex-1") {
                            label("block text-sm text-slate-400 mb-2") { +"Team Avatar (Optional)" }
                            
                            // Hidden file input
                            input(type = InputType.file) {
                                id = "team-avatar-input"
                                accept = "image/*"
                                classes = setOf("hidden")
                            }
                            
                            button(classes = "text-sm bg-dark-700 hover:bg-dark-600 text-white px-4 py-2 rounded-lg transition") {
                                onClickFunction = { 
                                    val input = document.getElementById("team-avatar-input") as? org.w3c.dom.HTMLInputElement
                                    input?.click()
                                }
                                +"Choose Image"
                            }
                            p("text-xs text-slate-500 mt-2") { +"100x100px, max 5MB" }
                        }
                    }
                }
                
                div("flex justify-end gap-3") {
                    button(classes = "px-4 py-2 rounded-xl text-slate-300 hover:bg-dark-700 transition") {
                        onClickFunction = { ModalManager.close() }
                        +"Cancel"
                    }
                    button(classes = "px-4 py-2 rounded-xl bg-brand-cyan text-white hover:bg-brand-green transition") {
                        onClickFunction = {
                            val nameInput = document.getElementById("team-name-input") as? org.w3c.dom.HTMLInputElement
                            val teamName = nameInput?.value?.trim() ?: ""
                            val userId = AppState.currentUser?.id
                            
                            when {
                                teamName.isEmpty() -> {
                                    kotlinx.browser.window.alert("Please enter a team name")
                                }
                                userId == null -> {
                                    kotlinx.browser.window.alert("You must be logged in to create a team")
                                }
                                else -> {
                                    // Check if team name exists first
                                    features.teams.data.TeamRepository.checkTeamNameExists(teamName)
                                        .then { exists ->
                                            if (exists) {
                                                kotlinx.browser.window.alert("Team name '$teamName' is already taken. Please choose another name.")
                                            } else {
                                                // Get avatar data if uploaded
                                                val preview = document.getElementById("team-avatar-preview") as? org.w3c.dom.HTMLImageElement
                                                val avatarData = if (preview != null && !preview.classList.contains("hidden")) {
                                                    val src = preview.src
                                                    console.log("📸 Avatar from preview, size: ${src.length} bytes")
                                                    src // This should be the compressed base64 data URL
                                                } else {
                                                    null
                                                }
                                                
                                                // Create team
                                                console.log("Creating team:", teamName, "with avatar:", avatarData != null)
                                                features.teams.data.TeamRepository.createTeam(teamName, userId, avatarData)
                                                    .then { teamId ->
                                                        console.log("✅ Team created with ID:", teamId)
                                                        
                                                        // Reload teams
                                                        AppState.teams.clear()
                                                        AppState.teamsLoaded = false
                                                        features.teams.data.TeamRepository.loadUserTeams(userId)
                                                            .then { teams ->
                                                                AppState.teams.addAll(teams)
                                                                AppState.teamsLoaded = true
                                                                
                                                                // Clear input
                                                                nameInput?.value = ""
                                                                val placeholder = document.getElementById("team-avatar-placeholder") as? org.w3c.dom.HTMLElement
                                                                preview?.classList?.add("hidden")
                                                                placeholder?.classList?.remove("hidden")
                                                                
                                                                // Close modal and navigate
                                                                ModalManager.close()
                                                                Router.render()
                                                            }
                                                    }
                                                    .catch { error ->
                                                        console.error("Error creating team:", error)
                                                        kotlinx.browser.window.alert("Failed to create team. Please try again.")
                                                    }
                                            }
                                        }
                                        .catch { error ->
                                            console.error("Error checking team name:", error)
                                            kotlinx.browser.window.alert("Error validating team name. Please try again.")
                                        }
                                }
                            }
                        }
                        +"Create"
                    }
                }
            }
        }
        
        // Setup image preview handler with compression
        kotlinx.browser.window.setTimeout({
            val fileInput = document.getElementById("team-avatar-input") as? org.w3c.dom.HTMLInputElement
            fileInput?.addEventListener("change", {
                val files = fileInput.files
                if (files != null && files.length > 0) {
                    val file = files.item(0)
                    if (file != null) {
                        val reader = org.w3c.files.FileReader()
                        reader.onload = {
                            val img = kotlinx.browser.document.createElement("img") as org.w3c.dom.HTMLImageElement
                            img.onload = {
                                // Create canvas for compression to 100x100
                                val canvas = kotlinx.browser.document.createElement("canvas") as org.w3c.dom.HTMLCanvasElement
                                canvas.width = 100
                                canvas.height = 100
                                
                                val ctx = canvas.getContext("2d") as org.w3c.dom.CanvasRenderingContext2D
                                ctx.drawImage(img, 0.0, 0.0, 100.0, 100.0)
                                
                                // Get compressed image as base64 (JPEG, 50% quality for smaller size)
                                val compressedDataUrl = canvas.toDataURL("image/jpeg", 0.5)
                                console.log("🗜️ Compressed image size: ${compressedDataUrl.length} bytes")
                                
                                // Update preview with compressed image
                                val preview = document.getElementById("team-avatar-preview") as? org.w3c.dom.HTMLImageElement
                                val placeholder = document.getElementById("team-avatar-placeholder") as? org.w3c.dom.HTMLElement
                                
                                preview?.src = compressedDataUrl
                                preview?.classList?.remove("hidden")

                                placeholder?.classList?.add("hidden")
                            }
                            js("img.src = reader.result")
                        }
                        reader.readAsDataURL(file)
                    }
                }
            })
        }, 100)
    }
}
